import { useEffect, useState } from "react";
import { Search, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PACKAGES, inr } from "@/data/content";
import { StatusPill } from "@/components/dashboard/kit";

const pkgName = (s) => PACKAGES.find((p) => p.slug === s)?.name || "—";

export default function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/earnings").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const onPay = async (r) => {
    try { await api.patch(`/admin/earnings/${r.id}`, { status: r.status === "paid" ? "pending" : "paid" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const filtered = rows.filter((r) => `${r.name} ${r.referrer_name || ""} ${r.referrer_code}`.toLowerCase().includes(q.toLowerCase()));
  const due = rows.filter((r) => r.status === "pending").reduce((a, r) => a + r.amount, 0);
  const paid = rows.filter((r) => r.status === "paid").reduce((a, r) => a + r.amount, 0);

  return (
    <div data-testid="admin-payouts-page">
      <h1 className="font-display text-3xl font-bold mb-1">Payouts</h1>
      <p className="text-sm text-[var(--muted)] mb-6">Referral commissions earned by your partners.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
        <div className="glass rounded-2xl p-5"><p className="text-xs text-[var(--muted)]">Commission due</p><p className="font-display text-2xl font-bold text-[var(--pink)] mt-1">{inr(due)}</p></div>
        <div className="glass rounded-2xl p-5"><p className="text-xs text-[var(--muted)]">Already paid</p><p className="font-display text-2xl font-bold mt-1">{inr(paid)}</p></div>
        <div className="glass rounded-2xl p-5 flex items-center gap-3"><div className="w-10 h-10 rounded-xl grid place-items-center text-[var(--on-accent)]" style={{ background: "var(--btn-bg)" }}><Wallet size={18} /></div><div><p className="text-xs text-[var(--muted)]">Entries</p><p className="font-bold text-lg">{rows.length}</p></div></div>
      </div>
      <label className="glass rounded-full px-4 py-2 flex items-center gap-2 text-sm w-fit mb-5"><Search size={15} className="text-[var(--muted)]" /><input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partner or member" className="bg-transparent outline-none w-56" /></label>
      <div className="glass rounded-2xl p-5 overflow-x-auto">
        {filtered.length === 0 ? <p className="text-sm text-[var(--muted)] py-8 text-center">No commissions yet.</p> : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs text-[var(--muted)]"><th className="pb-3">Partner</th><th>Joined member</th><th>Package</th><th>Commission</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>{filtered.map((r) => (
              <tr key={r.id} data-testid={`payout-row-${r.id}`} className="border-t border-[var(--surface-border)]">
                <td className="py-3 pr-4 font-semibold">{r.referrer_name}<br /><span className="text-xs text-[var(--muted)]">{r.referrer_code}</span></td>
                <td className="py-3 pr-4">{r.name}</td><td className="py-3 pr-4 text-xs">{pkgName(r.package)}</td>
                <td className="py-3 pr-4 font-bold text-[var(--pink)]">{inr(r.amount ?? 0)}</td><td className="py-3 pr-4"><StatusPill status={r.status === "pending" ? "unpaid" : r.status} /></td>
                <td className="py-3"><button data-testid={`payout-toggle-${r.id}`} onClick={() => onPay(r)} className={`${r.status === "paid" ? "btn-ghost" : "btn-neon"} !px-3 !py-1.5 text-xs`}>{r.status === "paid" ? "Mark unpaid" : "Mark paid"}</button></td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
